/* iter-marketing-roi · Sep-2026 · Marketing ROI card.
 *
 * Log monthly ad spend per channel and see CAC vs. LTV for whoever
 * that channel actually acquired — backed by `users.utm_source`
 * (captured at signup, see `lib/utmTracking.js`) joined against
 * lifetime `payment_transactions` revenue.
 *
 * "LTV" here is a 12-month projection off the cohort's observed
 * monthly run-rate, not a promise — young cohorts (a few days old)
 * will show noisy numbers until they've had time to convert/renew.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Megaphone, Plus, Trash2, Radio } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CHANNELS = ['google', 'facebook', 'tiktok', 'reddit', 'pinterest', 'other'];

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function RatioPill({ ratio }) {
  if (ratio === null || ratio === undefined) {
    return <span className="text-[11px] text-muted-foreground">—</span>;
  }
  const good = ratio >= 3;
  const ok   = ratio >= 1 && ratio < 3;
  return (
    <span
      data-testid="mroi-ratio-pill"
      className={`inline-flex items-center rounded-full px-2 py-[1px] text-[10.5px] font-semibold ${
        good ? 'bg-emerald-100 text-emerald-800' : ok ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
      }`}
    >
      {ratio.toFixed(1)}:1
    </span>
  );
}

export default function MarketingRoiCard() {
  const [roi, setRoi]           = useState(null);
  const [spendRows, setSpendRows] = useState([]);
  const [busy, setBusy]         = useState(false);
  const [channel, setChannel]   = useState('google');
  const [month, setMonth]       = useState(thisMonth());
  const [amount, setAmount]     = useState('');
  const [note, setNote]         = useState('');
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState(null);
  const [redditStatus, setRedditStatus] = useState(null);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const [roiRes, spendRes, redditRes] = await Promise.all([
        axios.get(`${API}/admin/marketing-roi`, { withCredentials: true }),
        axios.get(`${API}/admin/marketing-roi/spend`, { withCredentials: true }),
        axios.get(`${API}/admin/marketing-roi/reddit-capi/status`, { withCredentials: true }),
      ]);
      setRoi(roiRes.data);
      setSpendRows(spendRes.data || []);
      setRedditStatus(redditRes.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load Marketing ROI data.');
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const logSpend = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error('Enter a spend amount greater than $0.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/marketing-roi/spend`, {
        channel, month, amount_usd: amt, note: note.trim() || null,
      }, { withCredentials: true });
      toast.success(`Logged ${usd(amt)} for ${channel} (${month}).`);
      setAmount(''); setNote('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not log spend.');
    } finally { setSaving(false); }
  };

  const removeSpend = async (id) => {
    try {
      await axios.delete(`${API}/admin/marketing-roi/spend/${id}`, { withCredentials: true });
      await load();
    } catch { toast.error('Could not remove that entry.'); }
  };

  const rows = roi?.by_channel || [];

  return (
    <div className="mt-4 rounded-md border border-border bg-card" data-testid="admin-marketing-roi-card">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold">
          <Megaphone className="h-3.5 w-3.5" /> Marketing ROI — CAC vs. LTV by channel
        </span>
        <Button size="sm" variant="ghost" onClick={load} className="h-7 gap-1 text-[11px]" data-testid="mroi-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Oct-2026 — Reddit Conversions API status (feeds SIGN_UP events
       * back to Reddit for Reddit-attributed signups). */}
      {redditStatus && (
        <div
          className={
            'flex items-center gap-2 border-b border-border/60 px-3 py-1.5 text-[11px] ' +
            (redditStatus.configured ? 'text-muted-foreground' : 'text-amber-700')
          }
          data-testid="mroi-reddit-capi-status"
        >
          <Radio className="h-3 w-3 shrink-0" />
          {redditStatus.configured ? (
            <span>
              Reddit Conversions API: <b>{redditStatus.sent_30d}</b> sent
              {redditStatus.failed_30d > 0 && <span className="text-destructive"> · {redditStatus.failed_30d} failed</span>}
              {' '}(last 30 days)
              {redditStatus.last_event?.created_at && ` · last event ${new Date(redditStatus.last_event.created_at).toLocaleString()}`}
            </span>
          ) : (
            <span>Reddit Conversions API not configured — set REDDIT_PIXEL_ID + REDDIT_CONVERSION_ACCESS_TOKEN.</span>
          )}
        </div>
      )}

      {/* Log spend */}
      <div className="flex flex-wrap items-end gap-2 border-b border-border/60 px-3 py-2.5">
        <div>
          <label className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">Channel</label>
          <Select value={channel} onValueChange={setChannel}>
            <SelectTrigger className="h-8 w-[130px] text-[12px]" data-testid="mroi-channel-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CHANNELS.map((c) => <SelectItem key={c} value={c} data-testid={`mroi-channel-${c}`}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">Month</label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                  className="h-8 w-[130px] text-[12px]" data-testid="mroi-month-input" />
        </div>
        <div>
          <label className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">Spend ($)</label>
          <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00" className="h-8 w-[110px] text-[12px]" data-testid="mroi-amount-input" />
        </div>
        <div className="min-w-[160px] flex-1">
          <label className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">Note (optional)</label>
          <Input value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Nov-Dec holiday push" className="h-8 text-[12px]" data-testid="mroi-note-input" />
        </div>
        <Button size="sm" onClick={logSpend} disabled={saving} className="h-8 gap-1 text-[11px]" data-testid="mroi-log-spend-btn">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Log spend
        </Button>
      </div>

      {/* Logged spend history */}
      {spendRows.length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-b border-border/60 px-3 py-2">
          {spendRows.map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/50 px-2 py-0.5 text-[10.5px]"
              data-testid={`mroi-spend-row-${s.id}`}
            >
              <span className="font-medium capitalize">{s.channel}</span> {s.month} · {usd(s.amount_usd)}
              <button type="button" onClick={() => removeSpend(s.id)} data-testid={`mroi-remove-spend-${s.id}`}
                      title="Removes tracking only — the GL entry already posted stays in the Ledger."
                      className="text-muted-foreground hover:text-destructive">
                <Trash2 className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* CAC vs LTV table */}
      {busy && <div className="p-4 text-center text-[12px] text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></div>}
      {!busy && err && (
        <div className="p-4 text-center text-[12px] text-destructive" data-testid="mroi-error">{err}</div>
      )}
      {!busy && !err && rows.length === 0 && (
        <div className="p-4 text-center text-[12px] text-muted-foreground">
          No signups or spend logged in the last 90 days yet.
        </div>
      )}
      {!busy && !err && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-1.5 text-left">Channel</th>
                <th className="px-3 py-1.5 text-right">New users</th>
                <th className="px-3 py-1.5 text-right">Spend</th>
                <th className="px-3 py-1.5 text-right">CAC</th>
                <th className="px-3 py-1.5 text-right">Rev-to-date/user</th>
                <th className="px-3 py-1.5 text-right">Projected 12mo LTV</th>
                <th className="px-3 py-1.5 text-right">LTV : CAC</th>
                <th className="px-3 py-1.5 text-right">Payback</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.channel} className="border-t border-border/60" data-testid={`mroi-channel-row-${r.channel}`}>
                  <td className="px-3 py-1.5 font-mono-stat font-semibold capitalize">{r.channel}</td>
                  <td className="px-3 py-1.5 text-right">{r.new_users}</td>
                  <td className="px-3 py-1.5 text-right font-mono-stat">{usd(r.spend_usd)}</td>
                  <td className="px-3 py-1.5 text-right font-mono-stat">{r.cac_usd !== null ? usd(r.cac_usd) : '—'}</td>
                  <td className="px-3 py-1.5 text-right font-mono-stat text-muted-foreground">{usd(r.revenue_to_date_per_user_usd)}</td>
                  <td className="px-3 py-1.5 text-right font-mono-stat">{usd(r.projected_ltv_12mo_usd)}</td>
                  <td className="px-3 py-1.5 text-right"><RatioPill ratio={r.ltv_cac_ratio} /></td>
                  <td className="px-3 py-1.5 text-right font-mono-stat">{r.payback_months !== null ? `${r.payback_months} mo` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-3 py-2 text-[10.5px] text-muted-foreground">
            Blended CAC: {roi?.totals?.blended_cac_usd !== null ? usd(roi?.totals?.blended_cac_usd) : '—'} ·
            {' '}{roi?.totals?.new_users || 0} new users · window {roi?.period?.from} → {roi?.period?.to}.
            "LTV" is a 12-month projection off each cohort's observed run-rate — young cohorts will be noisy.
          </div>
        </div>
      )}
    </div>
  );
}
